import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import { financialEntryApi } from '../api';
import { FINANCIAL_VIEW_BY, ITEMS_PER_PAGE, getAccountLabel } from '../constants';
import { formatCalendarDateBR, formatDateTimeBR } from '../financialFormatters';
import {
  FinancialEntriesOutletContext,
  FinancialEntryListInstallment,
  FinancialEntryListItem,
  FinancialEntryListRow,
} from '../types';
import StatusBadge from './StatusBadge';
import ActionsDropdown from './ActionsDropdown';
import ViewFinancialEntryModal from '../ViewFinancialEntryModal';
import {
  CenteredCell,
  ErrorMessage,
  FooterLoadMore,
  GroupCellInner,
  ExpandArrow,
  MessageContainer,
  OrdenoGroupTr,
  OrdenoHeaderRow,
  OrdenoItemTr,
  OrdenoTable,
  OrdenoTd,
  OrdenoTdActions,
  OrdenoTh,
  OrdenoTr,
  TableCard,
  TableFooter,
  TableWrapper,
} from './style';

const SHARED_COLUMNS = [
  { key: 'createdAt', label: 'Registro' },
  { key: 'totalValue', label: 'Valor' },
  { key: 'company', label: 'Empresa' },
  { key: 'businessUnit', label: 'BU' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Conta a' },
  { key: 'dueDate', label: 'Vencimento' },
  { key: 'actions', label: '', center: true },
] as const;

const ENTRY_COLUMNS = [
  { key: 'id', label: 'Número' },
  ...SHARED_COLUMNS,
] as const;

const PAYMENT_COLUMNS = [
  { key: 'entryId', label: 'Número do Lançamento' },
  { key: 'id', label: 'Número' },
  ...SHARED_COLUMNS,
] as const;

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = formatDateTimeBR;

const FinancialEntriesTable: React.FC = () => {
  const {
    search,
    filters,
    refreshKey,
    triggerRefresh,
  } = useOutletContext<FinancialEntriesOutletContext>();

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [viewEntryId, setViewEntryId] = useState<number | null>(null);

  const viewBy = filters.viewBy ?? FINANCIAL_VIEW_BY.ENTRY;
  const isEntryView = viewBy === FINANCIAL_VIEW_BY.ENTRY;
  const isPaymentView = viewBy === FINANCIAL_VIEW_BY.PAYMENT;
  const isItemView = viewBy === FINANCIAL_VIEW_BY.ITEM;
  const columns = isPaymentView ? PAYMENT_COLUMNS : ENTRY_COLUMNS;

  useEffect(() => {
    setExpandedIds(new Set());
  }, [viewBy]);

  const queryFilters = useMemo(
    () => ({
      ...filters,
      search: search || undefined,
      limit: ITEMS_PER_PAGE,
      viewBy,
    }),
    [filters, search, viewBy],
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['financialEntries', queryFilters, refreshKey],
    queryFn: ({ pageParam = 1 }) =>
      financialEntryApi.list({ ...queryFilters, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const fetched = lastPage.page * lastPage.limit;
      if (fetched < lastPage.total) return lastPage.page + 1;
      return undefined;
    },
  });

  const rows: FinancialEntryListRow[] = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const total = data?.pages[0]?.total ?? 0;

  const handleEntryUpdated = () => {
    triggerRefresh();
    refetch();
  };

  const handleViewEntry = (entryId: number) => {
    setViewEntryId(entryId);
  };

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderInstallmentRow = (
    installment: FinancialEntryListInstallment,
    parent: FinancialEntryListRow,
  ) => (
    <OrdenoItemTr key={`${parent.id}-inst-${installment.id}`}>
      <OrdenoTd>{installment.installmentNumber?.trim() || '—'}</OrdenoTd>
      <OrdenoTd />
      <OrdenoTd>{formatCurrency(installment.value)}</OrdenoTd>
      <OrdenoTd>{parent.company ?? '—'}</OrdenoTd>
      <OrdenoTd>{parent.businessUnit ?? '—'}</OrdenoTd>
      <OrdenoTd>
        <StatusBadge status={parent.status} />
      </OrdenoTd>
      <OrdenoTd>{getAccountLabel(parent.type)}</OrdenoTd>
      <OrdenoTd>{formatCalendarDateBR(installment.dueDate)}</OrdenoTd>
      <OrdenoTdActions />
    </OrdenoItemTr>
  );

  const renderItemRow = (
    item: FinancialEntryListItem,
    parent: FinancialEntryListRow,
  ) => (
    <OrdenoItemTr key={`${parent.id}-item-${item.id}`}>
      <OrdenoTd>{item.id}</OrdenoTd>
      <OrdenoTd />
      <OrdenoTd>{formatCurrency(item.competenceValue)}</OrdenoTd>
      <OrdenoTd>{parent.company ?? '—'}</OrdenoTd>
      <OrdenoTd>{item.businessUnit ?? '—'}</OrdenoTd>
      <OrdenoTd>
        <StatusBadge status={parent.status} />
      </OrdenoTd>
      <OrdenoTd>{getAccountLabel(parent.type)}</OrdenoTd>
      <OrdenoTd>{formatCalendarDateBR(item.competenceDate)}</OrdenoTd>
      <OrdenoTdActions />
    </OrdenoItemTr>
  );

  const renderItemEntryRow = (row: FinancialEntryListRow) => {
    const items = row.items ?? [];
    const expandable = items.length > 0;
    const expanded = expandedIds.has(row.id);

    return (
      <React.Fragment key={row.id}>
        <OrdenoGroupTr
          $expandable={expandable}
          $expanded={expanded}
          onClick={() => {
            if (expandable) toggleExpanded(row.id);
          }}
        >
          <OrdenoTd>
            <GroupCellInner>
              <ExpandArrow $expanded={expanded} aria-hidden>
                {expanded ? '▼' : '▶'}
              </ExpandArrow>
              {row.id}
            </GroupCellInner>
          </OrdenoTd>
          <OrdenoTd>{formatDate(row.createdAt)}</OrdenoTd>
          <OrdenoTd>{formatCurrency(row.totalValue)}</OrdenoTd>
          <OrdenoTd>{row.company ?? '—'}</OrdenoTd>
          <OrdenoTd>{row.businessUnit ?? '—'}</OrdenoTd>
          <OrdenoTd>
            <StatusBadge status={row.status} />
          </OrdenoTd>
          <OrdenoTd>{getAccountLabel(row.type)}</OrdenoTd>
          <OrdenoTd>{formatCalendarDateBR(row.dueDate)}</OrdenoTd>
          <OrdenoTdActions onClick={(event) => event.stopPropagation()}>
            <CenteredCell>
              <ActionsDropdown
                entry={row}
                onView={handleViewEntry}
                onUpdated={handleEntryUpdated}
              />
            </CenteredCell>
          </OrdenoTdActions>
        </OrdenoGroupTr>

        {expandable && expanded &&
          items.map((item) => renderItemRow(item, row))}
      </React.Fragment>
    );
  };

  const renderEntryRow = (row: FinancialEntryListRow) => {
    const installments = row.installments ?? [];
    const expandable = installments.length > 0;
    const expanded = expandedIds.has(row.id);

    return (
      <React.Fragment key={row.id}>
        <OrdenoGroupTr
          $expandable={expandable}
          $expanded={expanded}
          onClick={() => {
            if (expandable) toggleExpanded(row.id);
          }}
        >
          <OrdenoTd>
            <GroupCellInner>
              <ExpandArrow $expanded={expanded} aria-hidden>
                {expanded ? '▼' : '▶'}
              </ExpandArrow>
              {row.id}
            </GroupCellInner>
          </OrdenoTd>
          <OrdenoTd>{formatDate(row.createdAt)}</OrdenoTd>
          <OrdenoTd>{formatCurrency(row.totalValue)}</OrdenoTd>
          <OrdenoTd>{row.company ?? '—'}</OrdenoTd>
          <OrdenoTd>{row.businessUnit ?? '—'}</OrdenoTd>
          <OrdenoTd>
            <StatusBadge status={row.status} />
          </OrdenoTd>
          <OrdenoTd>{getAccountLabel(row.type)}</OrdenoTd>
          <OrdenoTd>{formatCalendarDateBR(row.dueDate)}</OrdenoTd>
          <OrdenoTdActions onClick={(event) => event.stopPropagation()}>
            <CenteredCell>
              <ActionsDropdown
                entry={row}
                onView={handleViewEntry}
                onUpdated={handleEntryUpdated}
              />
            </CenteredCell>
          </OrdenoTdActions>
        </OrdenoGroupTr>

        {expandable && expanded &&
          installments.map((installment) => renderInstallmentRow(installment, row))}
      </React.Fragment>
    );
  };

  const renderPaymentRow = (row: FinancialEntryListRow) => {
    const displayValue = row.value ?? row.totalValue;

    return (
      <OrdenoTr key={row.id}>
        <OrdenoTd>{row.entryId ?? '—'}</OrdenoTd>
        <OrdenoTd>{row.id}</OrdenoTd>
        <OrdenoTd>{formatDate(row.createdAt)}</OrdenoTd>
        <OrdenoTd>{formatCurrency(displayValue)}</OrdenoTd>
        <OrdenoTd>{row.company ?? '—'}</OrdenoTd>
        <OrdenoTd>{row.businessUnit ?? '—'}</OrdenoTd>
        <OrdenoTd>
          <StatusBadge status={row.status} />
        </OrdenoTd>
        <OrdenoTd>{getAccountLabel(row.type)}</OrdenoTd>
        <OrdenoTd>{formatCalendarDateBR(row.dueDate)}</OrdenoTd>
        <OrdenoTdActions>
          <CenteredCell>
            <ActionsDropdown
              entry={row}
              onView={handleViewEntry}
              onUpdated={handleEntryUpdated}
            />
          </CenteredCell>
        </OrdenoTdActions>
      </OrdenoTr>
    );
  };

  if (isPending) {
    return (
      <TableCard>
        <MessageContainer>Carregando lançamentos...</MessageContainer>
      </TableCard>
    );
  }

  if (isError) {
    return (
      <TableCard>
        <ErrorMessage>
          Erro ao carregar lançamentos: {(error as Error)?.message ?? 'Tente novamente.'}
        </ErrorMessage>
      </TableCard>
    );
  }

  const footerLabel =
    total > 0
      ? `${rows.length} de ${total} lançamento${total !== 1 ? 's' : ''}`
      : 'Nenhum lançamento';

  return (
    <TableCard>
      <TableWrapper>
        <OrdenoTable>
          <thead>
            <OrdenoHeaderRow>
              {columns.map((col) => (
                <OrdenoTh key={col.key} $center={'center' in col ? col.center : false}>
                  {col.label}
                </OrdenoTh>
              ))}
            </OrdenoHeaderRow>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <OrdenoTr style={{ cursor: 'default' }}>
                <OrdenoTd colSpan={columns.length}>
                  <MessageContainer>Nenhum lançamento encontrado.</MessageContainer>
                </OrdenoTd>
              </OrdenoTr>
            ) : (
              rows.map((row) => {
                if (isEntryView) return renderEntryRow(row);
                if (isPaymentView) return renderPaymentRow(row);
                if (isItemView) return renderItemEntryRow(row);
                return null;
              })
            )}
          </tbody>
        </OrdenoTable>
      </TableWrapper>

      <TableFooter>
        <span>{footerLabel}</span>
      </TableFooter>

      {hasNextPage && (
        <FooterLoadMore>
          <LoadMoreButton
            currentCount={rows.length}
            totalCount={total}
            onLoadMore={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
            hasMore={!!hasNextPage}
            label="lançamentos"
          />
        </FooterLoadMore>
      )}

      {viewEntryId !== null && (
        <ViewFinancialEntryModal
          entryId={viewEntryId}
          onClose={() => setViewEntryId(null)}
          onUpdated={handleEntryUpdated}
        />
      )}
    </TableCard>
  );
};

export default FinancialEntriesTable;
